'use client'

import * as React from 'react'
import { useEffect, useState } from 'react'
import {
    ComposedChart,
    Line,
    Area,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine,
} from 'recharts'

import { Skeleton } from '@/components/ui/skeleton'
import { formatDateString } from '@/lib/utils/formate-date'
import { StockChartDataPoint } from '@/types/stock-data'
import { getStockChartDataAction } from '@/actions/stock-data'
import { mapLegacyTimeframeToNew } from '@/lib/utils/stock-data-transform'

interface WatchlistChartProps {
    companyId: number
    symbol?: string // Optional, only for gradient ID fallback
    className?: string
    width?: number | '100%'
    height?: number | '100%'
    period?: '1D' | 'W' | 'M' | '3M' | '1Y' | '5Y' | '10Y' | 'ALL'
    eodData?: StockChartDataPoint[] // Updated to use new type
    isLoading?: boolean
}

interface ChartDataPoint {
    date: string
    close: number
}

// Helper function to determine the best sampling period based on actual data span
function getBestSamplingPeriod(data: ChartDataPoint[]): string {
    if (data.length === 0) return 'W'

    const firstPoint = data[0]
    const lastPoint = data[data.length - 1]
    if (!firstPoint || !lastPoint) return 'W'

    const firstDate = new Date(firstPoint.date)
    const lastDate = new Date(lastPoint.date)
    const daysDiff = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24)

    // Return the appropriate period based on actual data span
    if (daysDiff <= 1) return '1D'
    if (daysDiff <= 7) return 'W'
    if (daysDiff <= 30) return 'M'
    if (daysDiff <= 90) return '3M'
    if (daysDiff <= 365) return '1Y'
    if (daysDiff <= 5 * 365) return '5Y'
    if (daysDiff <= 10 * 365) return '10Y'
    return 'ALL'
}

// Function to sample data points based on the best period for the actual data span
function sampleDataPoints(
    data: ChartDataPoint[],
    requestedPeriod: string,
    actualDataPeriod: string
): ChartDataPoint[] {
    if (data.length === 0) return data

    // Use the actual data period for sampling, not the requested period
    // This ensures consistent intervals based on how long the company has existed
    const samplingPeriod = actualDataPeriod

    switch (samplingPeriod) {
        case '1Y':
            // For 1 year of data, sample monthly (every 15th point)
            return data.filter((_, index) => index % 2 === 0 || index === data.length - 1)

        case '5Y':
            // For 5 years of data, sample monthly (every 15th point)
            return data.filter((_, index) => index % 7 === 0 || index === data.length - 1)

        case '10Y':
        case 'ALL':
            // For 10+ years of data, sample monthly (every 30th point)
            return data.filter((_, index) => index % 30 === 0 || index === data.length - 1)

        default:
            // For shorter periods (1D, W, M, 3M), use all data points (daily)
            return data
    }
}

// Custom YAxis tick component
const createCustomYAxisTick = () => {
    const CustomYAxisTick = (props: { x: number; y: number; payload: { value: number } }) => {
        const { x, y, payload } = props
        const value = payload.value

        return (
            <text
                x={x}
                y={y}
                dy={4}
                textAnchor="start"
                fill="rgba(0, 0, 0, 0.4)"
                opacity={1}
                className="text-caption1 fill-black/40 dark:fill-white/40"
            >
                {formatYAxisNumber(value)}
            </text>
        )
    }
    CustomYAxisTick.displayName = 'CustomYAxisTick'
    return CustomYAxisTick
}

// Custom XAxis tick component with padding for first tick
const createCustomXAxisTick = (period: string, actualPeriod?: string) => {
    const CustomXAxisTick = (props: {
        x: number
        y: number
        payload: { value: string }
        index: number
    }) => {
        const { x, y, payload, index } = props
        const date = new Date(payload.value)

        // Use actual period for formatting if provided (for newly listed companies)
        const formatPeriod = actualPeriod || period

        // Format date based on period (similar to your existing tickFormatter)
        let formattedDate = ''
        switch (formatPeriod) {
            case '1D':
                formattedDate = date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    hour12: true,
                })
                break
            case 'W':
            case '3M':
                formattedDate = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                })
                break
            case 'M':
            case '1Y':
                formattedDate = date.toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit',
                })
                break
            case '5Y':
            case '10Y':
            case 'ALL':
                formattedDate = date.toLocaleDateString('en-US', { year: 'numeric' })
                break
            default:
                formattedDate = date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    hour12: true,
                })
        }

        // Add padding only for the first tick (index === 0)
        const paddingX = index === 0 ? 10 : 0

        return (
            <text
                x={x + paddingX}
                y={y}
                dy={4}
                textAnchor="middle"
                fill="rgba(0, 0, 0, 0.4)"
                opacity={1}
                className="text-caption1 fill-black/40 dark:fill-white/40"
            >
                {formattedDate}
            </text>
        )
    }
    CustomXAxisTick.displayName = 'CustomXAxisTick'
    return CustomXAxisTick
}

// helper
function measureTextWidth(text: string, font = '9px system-ui, sans-serif') {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return 0
    ctx.font = font
    return ctx.measureText(text).width
}

// Number formatter for Y-axis labels
function formatYAxisNumber(value: number): string {
    const absValue = Math.abs(value)

    if (absValue >= 1000000000) {
        // For billions, use integer formatting
        return Math.round(value / 1000000000).toString() + 'B'
    } else if (absValue >= 1000000) {
        // For millions, use integer formatting
        return Math.round(value / 1000000).toString() + 'M'
    } else if (absValue >= 1000) {
        // For thousands, use integer formatting
        return Math.round(value / 1000).toString() + 'K'
    } else {
        // For values < 1000, use one decimal point precision
        return value.toFixed(1)
    }
}

export const MiniChart = React.memo(function MiniChart({
    companyId,
    symbol,
    className = '',
    width = '100%',
    height = '100%',
    period = '1Y',
    eodData,
    isLoading = false,
}: WatchlistChartProps) {
    const [data, setData] = useState<ChartDataPoint[]>([])
    const [error, setError] = useState<string | null>(null)
    const [actualPeriod, setActualPeriod] = useState<string>(period)

    const closePrices = data.map((point) => point.close)
    const minPrice = Math.min(...closePrices)
    const maxPrice = Math.max(...closePrices)
    // Removed unused padding and yDomain variables

    const [yAxisWidth, setYAxisWidth] = React.useState(24) // sensible default

    // Calculate appropriate interval based on actual period and data length
    const calculateXAxisInterval = (actualPeriod: string, dataLength: number) => {
        switch (actualPeriod) {
            case '1D':
                // Show ~6-8 labels for 1 day (reduce clutter for intraday data)
                return Math.floor((dataLength - 1) / 6)
            case 'W':
                // Show ~4-5 labels for 1 week
                return Math.floor((dataLength - 1) / 4)
            case 'M':
                return Math.floor((dataLength - 1) / 8) // Show ~8 ticks for 1 month
            case '3M':
                return Math.floor((dataLength - 1) / 6) // Show ~6 ticks for 3 months
            case '1Y':
                return Math.floor((dataLength - 1) / 4) // Show ~4 ticks for 1 year
            case '5Y':
            case '10Y':
            case 'ALL':
                return Math.floor((dataLength - 1) / 3) // Show ~3 ticks for longer periods
            default:
                return Math.floor((dataLength - 1) / 3.5)
        }
    }

    useEffect(() => {
        if (data.length === 0) return

        // Get all potential Y-axis tick values by calculating domain and tick values
        const dataMin = Math.floor(minPrice) - 1
        const dataMax = Math.ceil(maxPrice) + 1
        const range = dataMax - dataMin

        // Generate potential tick values (Recharts typically creates 5-6 ticks)
        const tickCount = 5
        const tickInterval = range / (tickCount - 1)
        const potentialTicks = []

        for (let i = 0; i < tickCount; i++) {
            potentialTicks.push(dataMin + i * tickInterval)
        }

        // Add corner cases (actual min/max values)
        const candidates = [
            ...potentialTicks,
            minPrice,
            maxPrice,
            data[0]?.close ?? 0,
            data[data.length - 1]?.close ?? 0,
        ].map(formatYAxisNumber)

        const widest = candidates.reduce((m, s) => Math.max(m, measureTextWidth(s)), 0)

        // Add extra padding for right-aligned text and margin
        setYAxisWidth(Math.ceil(widest) + 12)
    }, [data, minPrice, maxPrice])

    useEffect(() => {
        if (eodData) {
            // Use passed eodData
            if (eodData.length === 0) {
                setError('No data available')
                setData([])
                setActualPeriod(period)
                return
            }

            // Convert StockChartDataPoint to chart format
            const chartData: ChartDataPoint[] = eodData.map((point) => ({
                date: point.date,
                close: point.close,
            }))

            // Determine the best sampling period based on actual data span
            const bestSamplingPeriod = getBestSamplingPeriod(chartData)

            // Set the actual period for X-axis formatting based on the better of requested vs actual period
            const displayPeriod = ['1D', 'W', 'M', '3M'].includes(period)
                ? period
                : bestSamplingPeriod
            setActualPeriod(displayPeriod)

            // Apply data sampling based on the best period for this data span
            const sampledData = sampleDataPoints(chartData, period, bestSamplingPeriod)
            setData(sampledData)
            setError(null)
        } else if (companyId) {
            // Fetch data using company_id if not provided
            const fetchData = async () => {
                try {
                    setError(null)
                    const timeframe = mapLegacyTimeframeToNew(period)
                    const response = await getStockChartDataAction({ companyId, timeframe })

                    if (!response.data || response.data.length === 0) {
                        setError('No data available')
                        setData([])
                        return
                    }

                    const chartData: ChartDataPoint[] = response.data.map((point) => ({
                        date: point.date,
                        close: point.close,
                    }))

                    const bestSamplingPeriod = getBestSamplingPeriod(chartData)
                    const displayPeriod = ['1D', 'W', 'M', '3M'].includes(period)
                        ? period
                        : bestSamplingPeriod
                    setActualPeriod(displayPeriod)

                    const sampledData = sampleDataPoints(chartData, period, bestSamplingPeriod)
                    setData(sampledData)
                } catch {
                    setError('Failed to load data')
                    setData([])
                }
            }
            fetchData()
        } else {
            // No eodData or companyId provided - show error state
            setError('No data provided')
            setData([])
            setActualPeriod(period)
        }
    }, [companyId, period, eodData])

    // Show skeleton while loading
    if (isLoading) {
        return (
            <Skeleton
                className={className}
                style={{
                    width: typeof width === 'number' ? `${width}px` : width,
                    height: typeof height === 'number' ? `${height}px` : height,
                }}
            />
        )
    }

    // Show error state
    if (error || data.length === 0) {
        return (
            <div
                className={`${className} flex items-center justify-center bg-muted dark:bg-muted rounded-sm`}
                style={{
                    width: typeof width === 'number' ? `${width}px` : width,
                    height: typeof height === 'number' ? `${height}px` : height,
                }}
            >
                <div className="text-xs text-muted-foreground dark:text-muted-foreground">N/A</div>
            </div>
        )
    }

    // Calculate if trend is up or down
    const firstPrice = data[0]?.close || 0
    const lastPrice = data[data.length - 1]?.close || 0
    const isUp = lastPrice >= firstPrice

    // Define chart colors
    const chartGreen = 'rgb(34, 197, 94)' // Tailwind green-500
    const chartRed = 'rgb(239, 68, 68)' // Tailwind red-500

    return (
        <div
            className={className}
            style={{
                width: typeof width === 'number' ? `${width}px` : width,
                height: typeof height === 'number' ? `${height}px` : height,
            }}
        >
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={data}
                    margin={{ top: 0, right: -5, left: 0, bottom: -17 }} // no big right margin
                >
                    <CartesianGrid stroke="hsl(var(--border))" opacity={0.5} horizontal={false} />
                    <ReferenceLine
                        y={data[0]?.close || 0}
                        stroke={isUp ? chartGreen : chartRed}
                        strokeDasharray="3 3"
                        opacity={0.5}
                    />
                    <defs>
                        <linearGradient
                            id={`gradient-${symbol || companyId}-${isUp ? 'up' : 'down'}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor={isUp ? chartGreen : chartRed}
                                stopOpacity={0.4}
                            />
                            <stop
                                offset="50%"
                                stopColor={isUp ? chartGreen : chartRed}
                                stopOpacity={0.2}
                            />
                            <stop
                                offset="100%"
                                stopColor={isUp ? chartGreen : chartRed}
                                stopOpacity={0.05}
                            />
                        </linearGradient>
                    </defs>
                    <XAxis
                        dataKey="date"
                        hide={false}
                        axisLine={false}
                        tickLine={false}
                        tick={createCustomXAxisTick(
                            period,
                            actualPeriod !== period ? actualPeriod : undefined
                        )}
                        interval={calculateXAxisInterval(actualPeriod, data.length)}
                    />
                    <YAxis
                        width={yAxisWidth} // dynamically calculated width
                        orientation="right"
                        axisLine={false}
                        tickLine={false}
                        tickMargin={6} // increased margin for better spacing
                        domain={[
                            (dataMin: number) => Math.floor(dataMin) - 1,
                            (dataMax: number) => Math.ceil(dataMax) + 1,
                        ]}
                        tick={createCustomYAxisTick()}
                        allowDecimals={false}
                    />
                    <Tooltip
                        content={({ active, payload, label }) => {
                            if (active && payload && payload.length && label) {
                                return (
                                    <div className="bg-background dark:bg-background border border-border dark:border-border rounded-lg p-2 shadow-lg text-xs">
                                        <p className="font-medium">
                                            {formatDateString(String(label))}
                                        </p>
                                        <p>${Number(payload[0].value).toFixed(2)}</p>
                                    </div>
                                )
                            }
                            return null
                        }}
                    />
                    {/* Area for gradient background */}
                    <Area
                        type="linear"
                        dataKey="close"
                        stroke="none"
                        fill={`url(#gradient-${symbol || companyId}-${isUp ? 'up' : 'down'})`}
                        fillOpacity={1}
                    />
                    {/* Main line */}
                    <Line
                        type="linear"
                        dataKey="close"
                        stroke={isUp ? chartGreen : chartRed}
                        strokeWidth={1}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        dot={false}
                        activeDot={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    )
})
